/**
 * Contractual alerts — milestones, obligation deadlines, time-bars, LD caps and
 * Cognara red flags on one filterable timeline.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertOctagon, AlertTriangle, CalendarClock, Loader2, Flag, Gavel, FileClock, Info } from 'lucide-react';
import api from '@/lib/api';
import { fmtDate } from './portfolioUi';

const SEV = {
  BREACH: { tone: '#B91C1C', label: 'Breach', icon: AlertOctagon },
  WARNING: { tone: '#B45309', label: 'Warning', icon: AlertTriangle },
  INFO: { tone: '#15803D', label: 'On track', icon: Info },
};

const KIND_ICON = {
  MILESTONE: CalendarClock, OBLIGATION: FileClock, LD: Gavel,
  TIME_BAR: AlertTriangle, CLAIM: Gavel, RED_FLAG: Flag,
};

export const ContractAlertsPanel = ({ contractId }) => {
  const [data, setData] = useState(null);
  const [sev, setSev] = useState('ALL');
  const [kind, setKind] = useState('ALL');

  useEffect(() => {
    api.get(`/contracts/contracts/${contractId}/alerts`).then(({ data }) => setData(data)).catch(() => setData({ items: [], counts: {} }));
  }, [contractId]);

  const kinds = useMemo(() => Array.from(new Set((data?.items || []).map(i => i.kind))), [data]);
  const items = useMemo(() => (data?.items || []).filter(i =>
    (sev === 'ALL' || i.severity === sev) && (kind === 'ALL' || i.kind === kind)), [data, sev, kind]);

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand-accent)' }} /></div>;

  return (
    <div className="space-y-5" data-testid="contract-alerts-panel">
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(SEV).map(([key, s]) => (
          <button key={key} onClick={() => setSev(sev === key ? 'ALL' : key)}
                  className="surface-card rounded-xl px-5 py-4 text-left relative overflow-hidden"
                  style={{ outline: sev === key ? `1px solid ${s.tone}` : 'none' }}
                  data-testid={`alert-sev-${key.toLowerCase()}`}>
            <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: s.tone }} />
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
            <p className="text-3xl font-light tabular-nums mt-2" style={{ color: s.tone, fontFamily: '"JetBrains Mono", monospace' }}>
              {data.counts?.[key] ?? 0}
            </p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {['ALL', ...kinds].map(k => (
          <button key={k} onClick={() => setKind(k)}
                  className="rounded-full border px-3 py-1 text-[11px]"
                  style={{ borderColor: kind === k ? 'var(--brand-accent)' : 'var(--border-color)',
                           color: kind === k ? 'var(--text-primary)' : 'var(--text-secondary)' }}
                  data-testid={`alert-kind-${k.toLowerCase()}`}>
            {k.replace(/_/g, ' ').toLowerCase()}
          </button>
        ))}
        {sev !== 'ALL' && (
          <button onClick={() => setSev('ALL')} className="text-[11px] ml-2" style={{ color: 'var(--brand-accent)' }}
                  data-testid="alert-clear-filter">clear severity filter</button>
        )}
      </div>

      <div className="surface-card rounded-xl divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
        {items.map((i, idx) => {
          const s = SEV[i.severity] || SEV.INFO;
          const Icon = KIND_ICON[i.kind] || Info;
          return (
            <div key={idx} className="flex items-start gap-4 px-5 py-4" data-testid={`alert-item-${idx}`}>
              <span className="mt-0.5 rounded-full p-1.5 shrink-0" style={{ background: `${s.tone}18` }}>
                <Icon className="w-3.5 h-3.5" style={{ color: s.tone }} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{i.title}</p>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{i.detail}</p>
              </div>
              <div className="text-right shrink-0 max-w-[180px]">
                <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                  {!i.due ? '—' : Number.isNaN(Date.parse(i.due)) ? String(i.due) : fmtDate(i.due)}
                </p>
                {i.days != null && (
                  <p className="text-[11px] tabular-nums font-semibold" style={{ color: s.tone }}>
                    {i.days < 0 ? `${Math.abs(i.days)}d overdue` : `${i.days}d left`}
                  </p>
                )}
              </div>
            </div>
          );
        })}
        {!items.length && (
          <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }} data-testid="alerts-empty">
            Nothing matches this filter.
          </p>
        )}
      </div>
    </div>
  );
};

export default ContractAlertsPanel;
